import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  Calendar, 
  Users, 
  Camera, 
  TrendingUp,
  Plus,
  Eye
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { data: events, isLoading: eventsLoading } = useQuery(
    'events',
    () => axios.get('/api/events').then(res => res.data.data)
  );

  const stats = React.useMemo(() => {
    if (!events) return null;
    
    return {
      totalEvents: events.length,
      activeEvents: events.filter((e: any) => e.isActive).length,
      totalPhotos: events.reduce((sum: number, e: any) => sum + (e.stats?.totalPhotos || 0), 0),
      recentEvents: events.slice(0, 5)
    };
  }, [events]);

  const StatCard = ({ title, value, icon: Icon, color }: any) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-md ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="mr-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-lg font-medium text-gray-900">{value}</dd>
          </dl>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              דשבורד
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              סקירה כללית של המערכת והאירועים
            </p>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            <Link
              to="/events"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
            >
              <Plus className="h-4 w-4 ml-2" />
              אירוע חדש
            </Link>
          </div>
        </div>

        {/* Stats */}
        {eventsLoading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="סך הכל אירועים"
              value={stats?.totalEvents || 0}
              icon={Calendar}
              color="bg-blue-500"
            />
            <StatCard
              title="אירועים פעילים"
              value={stats?.activeEvents || 0}
              icon={TrendingUp}
              color="bg-green-500"
            />
            <StatCard
              title="סך הכל תמונות"
              value={stats?.totalPhotos || 0}
              icon={Camera}
              color="bg-purple-500"
            />
            <StatCard
              title="משתמשים"
              value="0"
              icon={Users}
              color="bg-orange-500"
            />
          </div>
        )}

        {/* Recent Events */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">אירועים אחרונים</h3>
              <Link
                to="/events"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                צפה בכל האירועים
              </Link>
            </div>
            
            {eventsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex space-x-4">
                    <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : stats?.recentEvents?.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-8">
                  {stats?.recentEvents?.map((event: any, eventIdx: number) => (
                    <li key={event._id}>
                      <div className="relative pb-8">
                        {eventIdx !== (stats?.recentEvents?.length || 0) - 1 ? (
                          <span
                            className="absolute top-4 right-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                              event.isActive ? 'bg-green-500' : 'bg-gray-400'
                            }`}>
                              <Calendar className="h-4 w-4 text-white" />
                            </span>
                          </div>
                          <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                            <div>
                              <p className="text-sm text-gray-900">
                                {event.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {event.coupleNames.bride} & {event.coupleNames.groom}
                              </p>
                            </div>
                            <div className="whitespace-nowrap text-left text-sm text-gray-500">
                              <Link
                                to={`/events/${event._id}`}
                                className="text-blue-600 hover:text-blue-500"
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">אין אירועים</h3>
                <p className="mt-1 text-sm text-gray-500">התחל על ידי יצירת אירוע חדש</p>
                <div className="mt-6">
                  <Link
                    to="/events"
                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                  >
                    <Plus className="h-4 w-4 ml-2" />
                    צור אירוע חדש
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
